// Full-page drag-and-drop target for .arch files / project folders.
// Mounts no visible chrome; renders an overlay only while a file
// drag is in flight. Single instance per app — file inputs live in
// SourceMenu (the brand-menu dropdown), not here.

import { useEffect, useRef, useState } from "react";
import { dataTransferToVfs, type LoadedSources } from "../sourceLoader";

export interface SourceDropTargetProps {
  readonly onLoaded: (sources: LoadedSources) => void;
}

export function SourceDropTarget({ onLoaded }: SourceDropTargetProps) {
  const [dragOver, setDragOver] = useState(false);
  // Drag events fire on every nested element; count enter/leave so
  // the overlay only hides when the cursor genuinely leaves the page.
  const dragDepth = useRef(0);

  useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (!Array.from(e.dataTransfer.types).includes("Files")) return;
      dragDepth.current += 1;
      setDragOver(true);
    };
    const onDragLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragOver(false);
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (!Array.from(e.dataTransfer.types).includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onDrop = async (e: DragEvent) => {
      if (!e.dataTransfer) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragOver(false);
      const loaded = await dataTransferToVfs(e.dataTransfer);
      if (loaded.vfs.size === 0) {
        console.warn("[arch] drop: no .arch or package.archspace files");
        return;
      }
      onLoaded(loaded);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [onLoaded]);

  if (!dragOver) return null;

  return (
    <div
      className="fixed inset-0 z-overlay pointer-events-none flex items-center justify-center bg-slate-950/(--tint-strong) backdrop-blur-xs"
      data-hud
    >
      <div className="px-8 py-6 rounded-2xl border-2 border-dashed border-sky-400 bg-slate-900/(--tint-heavy) text-sky-200 text-lg font-medium shadow-arch-glow-lg">
        drop .arch files or a project folder
      </div>
    </div>
  );
}
